import React, { useMemo, useState } from "react";
import { useQuery } from "@apollo/client";
import { GET_USER_ORDERS } from "../../api/graphql/orders";
import {
  AndroidFilled,
  AppleFilled,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import styles from "./styles.module.scss";
import { useUser } from '../../context/UserContext';
import CampaignFilters from "./components/CampaignFilters";
import CampaignTable from "./components/CampaignTable";
import Page from "../Page";
import { CampaignSuperAdmin, DateFilterType, DateRange } from "./types";
import {
  formatOrderType,
  getTotalInstalls,
  matchesOrderType,
} from "../../utils/formatters";
import { isInternalUser } from "../../utils/internalUsers";
import useOrderManagers, {
  ALL_MANAGERS,
  managerFilterOptions,
  matchesManager,
} from "../ManagerSelect/useOrderManagers";

dayjs.extend(isBetween);

export function getStoreIcon(store: any) {
  switch (store) {
    case "GOOGLE_STORE":
      return (
        <AndroidFilled className={styles.platformIcon} />
      );
    case "APP_STORE":
      return <AppleFilled className={styles.platformIcon} />;
  }
}

const CampaignSuperAdminTable: React.FC = () => {
  const { data: campaignsList, loading } = useQuery(GET_USER_ORDERS);
  const [searchApp, setSearchApp] = useState("");
  const [periodFilter, setPeriodFilter] = useState<DateFilterType | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({
    start: null,
    end: null,
  });
  const [selectedCountry, setSelectedCountry] = useState("all");
  const [selectedPlatform, setSelectedPlatform] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedManager, setSelectedManager] = useState(ALL_MANAGERS);
  // Here and not in the table: the manager filter reads the saved choices too.
  const managers = useOrderManagers();
  const { orderManager, effectiveManager } = managers;
  const managerOptions = useMemo(
    () => managerFilterOptions(campaignsList?.getAllOrders || [], orderManager),
    [campaignsList, orderManager],
  );
  const [showInternal, setShowInternal] = useState(false);
  const { user } = useUser();
  const navigate = useNavigate();
  const isBuyVisible = !user || user.role === "SUPER_ADMIN";

  const parseDateString = (input?: string) => {
    if (!input) return null;
    const normalized = input.includes(".")
      ? input.split(".")
      : input.includes("/")
        ? input.split("/")
        : null;
    if (normalized && normalized.length === 3) {
      const [day, month, year] = normalized;
      return dayjs(`${year}-${month}-${day}`);
    }
    if (!Number.isNaN(Number(input))) {
      return dayjs(Number(input));
    }
    return dayjs(input);
  };

  const getComparableDate = (item: CampaignSuperAdmin, key: "initialDate" | "endDate") => {
    if (key === "initialDate") {
      return (
        parseDateString(item.initialDate)?.valueOf() ||
        (item.createdAt ? Number(item.createdAt) : 0)
      );
    }
    return parseDateString(item.endDate)?.valueOf() || 0;
  };

  const formatStatusLabel = (status: string) =>
    status
      .toLowerCase()
      .split(/[_\s]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");

  const countryOptions = useMemo(() => {
    const data = (campaignsList?.getAllOrders as CampaignSuperAdmin[]) || [];
    let regionNames: Intl.DisplayNames | null = null;
    try {
      regionNames = new Intl.DisplayNames(["en"], { type: "region" });
    } catch {
      regionNames = null;
    }
    const getCountryName = (code: string) => {
      const upper = code.toUpperCase();
      try {
        return regionNames?.of(upper) || upper;
      } catch {
        return upper;
      }
    };
    const unique = new Map<string, string>();
    data.forEach((item) => {
      const country = item.app?.country?.trim();
      if (country) unique.set(country.toLowerCase(), getCountryName(country));
    });
    return [
      { value: "all", label: "All countries" },
      ...Array.from(unique.entries())
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([value, label]) => ({ value, label })),
    ];
  }, [campaignsList]);

  const statusOptions = useMemo(() => {
    const data = (campaignsList?.getAllOrders as CampaignSuperAdmin[]) || [];
    const unique = new Map<string, string>();
    data.forEach((item) => {
      const status = item.status?.trim();
      if (status) unique.set(status.toLowerCase(), formatStatusLabel(status));
    });
    return [
      { value: "all", label: "All statuses" },
      ...Array.from(unique.entries())
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([value, label]) => ({ value, label })),
    ];
  }, [campaignsList]);

  const filteredData = useMemo(() => {
    const data = (campaignsList?.getAllOrders as CampaignSuperAdmin[]) || [];
    return data.filter((item: any) => {
      // Internal (test) accounts stay out unless asked for
      if (!showInternal && isInternalUser(item.user)) return false;

      // Status filter
      const matchesStatus =
        selectedStatus === "all" ||
        item.status?.toLowerCase() === selectedStatus.toLowerCase();

      // Country filter
      const matchesCountry =
        selectedCountry === "all" ||
        item.app?.country?.toLowerCase() === selectedCountry.toLowerCase();

      // Platform filter
      const matchesPlatform =
        selectedPlatform === "all" ||
        item.app?.store?.toLowerCase() === selectedPlatform.toLowerCase();

      const matchesType = matchesOrderType(
        selectedType,
        item.orderType,
        item.subtype,
      );

      // The manager the Manager column shows: the order's own, else the client's.
      const matchesManagerFilter = matchesManager(
        selectedManager,
        effectiveManager(item),
      );

      // Order ID / Email filter
      const matchesOrderId =
        !searchApp ||
        (() => {
          const searchValue = searchApp.trim().toLowerCase();
          const hashId = String(item.hashId || "");
          const orderId = String(item.id || "");
          const email = (item.user?.email || "").toLowerCase();

          return (
            hashId.includes(searchValue) ||
            orderId.includes(searchValue) ||
            email.includes(searchValue)
          );
        })();

      // Date filter
      const matchesDate = (() => {
        const itemDate =
          parseDateString(item.initialDate) ||
          (item.createdAt ? parseDateString(item.createdAt) : null);
        // No active date filter selected — keep the row regardless of date.
        if (!dateRange.start && !dateRange.end && !periodFilter) {
          return true;
        }
        // Date filter is active but the row has no parseable date — keep it
        // visible instead of silently dropping it.
        if (!itemDate || !itemDate.isValid()) {
          return true;
        }

        if (dateRange.start && dateRange.end) {
          const start = dayjs(dateRange.start);
          const end = dayjs(dateRange.end);
          if (!start.isValid() || !end.isValid()) return true;
          return itemDate.isBetween(start, end, "day", "[]");
        }

        if (periodFilter) {
          const now = dayjs();
          switch (periodFilter) {
            case "week":
              return itemDate.isBetween(
                now.startOf("week"),
                now.endOf("week"),
                "day",
                "[]",
              );
            case "month":
              return itemDate.isBetween(
                now.startOf("month"),
                now.endOf("month"),
                "day",
                "[]",
              );
            case "quarter": {
              const quarterStartMonth = Math.floor(now.month() / 3) * 3;
              const start = now.month(quarterStartMonth).startOf("month");
              const end = start.add(2, "month").endOf("month");
              return itemDate.isBetween(start, end, "day", "[]");
            }
            default:
              return true;
          }
        }

        return true;
      })();

      return (
        matchesStatus &&
        matchesCountry &&
        matchesPlatform &&
        matchesType &&
        matchesManagerFilter &&
        matchesOrderId &&
        matchesDate
      );
    });
  }, [
    campaignsList,
    periodFilter,
    dateRange,
    selectedCountry,
    selectedPlatform,
    selectedStatus,
    selectedType,
    selectedManager,
    effectiveManager,
    searchApp,
    showInternal,
  ]);

  // The table starts again from the top when a filter changes.
  const filtersKey = JSON.stringify([
    searchApp,
    periodFilter,
    dateRange,
    selectedCountry,
    selectedPlatform,
    selectedStatus,
    selectedType,
    selectedManager,
    showInternal,
  ]);

  // Обработчик выбора периода
  const handlePeriodChange = (value: DateFilterType | null) => {
    setPeriodFilter(value);
    setDateRange({ start: null, end: null });
  };

  const handleDateChange = (field: "start" | "end", value: string) => {
    setDateRange((prev) => ({
      ...prev,
      [field]: value || null,
    }));
    if (value) {
      setPeriodFilter(null);
    }
  };

  const totals = useMemo(() => {
    return filteredData.reduce(
      (acc, { orderPrice = 0, buyingPrice = 0 }) => {
        acc.sell += Number(orderPrice) || 0;
        acc.buy += Number(buyingPrice) || 0;
        return acc;
      },
      { sell: 0, buy: 0 },
    );
  }, [filteredData]);

  const totalInstalls = useMemo(
    () => getTotalInstalls(filteredData),
    [filteredData],
  );

  const formatCurrency = (value?: number | null, symbol = "$") => {
    const numericValue = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(numericValue)) {
      return `${symbol}0.00`;
    }
    return `${symbol}${numericValue.toFixed(2)}`;
  };

  const formatDateCell = (value?: string, fallback?: string) => {
    const parsed = parseDateString(value) || (fallback ? dayjs(fallback) : null);
    return parsed?.isValid() ? parsed.format("DD.MM.YYYY") : "N/A";
  };

  const getDetailsPath = (record: CampaignSuperAdmin) =>
    `/campaigns/${record.orderType}/${record.id}`;

  const handleUserClick = (userId: number) => {
    navigate(`/user/${userId}`);
  };

  if (loading) return <Page title="Campaigns 2.0">Loading...</Page>;

  return (
    <Page title="Campaigns 2.0" fitScreen>
      <CampaignFilters
        searchValue={searchApp}
        onSearchChange={setSearchApp}
        periodFilter={periodFilter}
        onPeriodChange={handlePeriodChange}
        dateRange={dateRange}
        onDateChange={handleDateChange}
        selectedCountry={selectedCountry}
        selectedPlatform={selectedPlatform}
        selectedStatus={selectedStatus}
        selectedType={selectedType}
        selectedManager={selectedManager}
        onCountryChange={setSelectedCountry}
        onPlatformChange={setSelectedPlatform}
        onStatusChange={setSelectedStatus}
        onTypeChange={setSelectedType}
        onManagerChange={setSelectedManager}
        managerOptions={managerOptions}
        countryOptions={countryOptions}
        statusOptions={statusOptions}
        showInternal={showInternal}
        onShowInternalChange={setShowInternal}
      />
      <CampaignTable
        data={filteredData}
        filtersKey={filtersKey}
        managers={managers}
        isBuyVisible={isBuyVisible}
        totals={totals}
        totalInstalls={totalInstalls}
        formatCurrency={formatCurrency}
        formatDateCell={formatDateCell}
        getComparableDate={getComparableDate}
        getStoreIcon={getStoreIcon}
        getDetailsPath={getDetailsPath}
        onUserClick={handleUserClick}
      />
    </Page>
  );
};

export default CampaignSuperAdminTable;
